import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MapPin, Phone, Mail, ArrowRight } from 'lucide-react';
import { FaLinkedin, FaTwitter, FaFacebook, FaInstagram, FaYoutube, FaPinterest } from 'react-icons/fa';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [socialLinks, setSocialLinks] = useState({
    linkedin: '',
    twitter: '',
    facebook: '',
    instagram: '',
    youtube: '',
    pinterest: ''
  });

  useEffect(() => {
    const fetchSocialLinks = async () => {
      try {
        const res = await axios.get(`${API}/site-config`);
        if (res.data?.social) {
          setSocialLinks(res.data.social);
        }
      } catch (error) {
        console.error('Failed to fetch social links:', error);
      }
    };
    fetchSocialLinks();
  }, []);

  const quickLinks = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Careers', path: '/jobs' },
    { name: 'Culture', path: '/culture' },
    { name: 'Contact', path: '/contact' },
  ];

  const services = [
    { name: 'Recruitment Process Outsourcing', path: '/services#rpo' },
    { name: 'Staffing Solutions', path: '/services#staffing' },
    { name: 'HR Consulting', path: '/services#consulting' },
    { name: 'Executive Search', path: '/services#executive' },
  ];

  const resources = [
    { name: 'Apply for Jobs', path: '/apply' },
    { name: 'Refer a Candidate', path: '/refer' },
    { name: 'Track Application', path: '/track' },
    { name: 'Privacy Policy', path: '/privacy' },
    { name: 'Terms of Service', path: '/terms' },
  ];

  // Social media icons mapping
  const socialIcons = [
    { key: 'linkedin', icon: FaLinkedin, label: 'LinkedIn' },
    { key: 'twitter', icon: FaTwitter, label: 'Twitter' },
    { key: 'facebook', icon: FaFacebook, label: 'Facebook' },
    { key: 'instagram', icon: FaInstagram, label: 'Instagram' },
    { key: 'youtube', icon: FaYoutube, label: 'YouTube' },
    { key: 'pinterest', icon: FaPinterest, label: 'Pinterest' },
  ];

  // Filter to only show social links that have URLs
  const activeSocialLinks = socialIcons.filter(social => socialLinks[social.key]);

  return (
    <footer className="bg-gradient-to-b from-[#1E3A5F] to-[#0F2137] text-white" data-testid="footer">
      {/* Wave Divider */}
      <div className="relative -mt-1">
        <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full">
          <path d="M0 100V50C240 83.3333 480 100 720 100C960 100 1200 83.3333 1440 50V100H0Z" fill="#1E3A5F"/>
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-12">
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-10 lg:gap-6">
          {/* Company Info */}
          <div className="lg:col-span-2">
            <Link to="/" className="inline-flex items-center gap-3 mb-6 group">
              {/* Logo */}
              <img
                src="https://customer-assets.emergentagent.com/job_tech5-recruitment/artifacts/b0zdbykt_IMG_3384.jpg"
                alt="Tech5sages"
                className="h-14 w-14 object-contain rounded-xl shadow-lg transition-transform duration-300 group-hover:scale-105"
              />
              {/* Company Name */}
              <div className="flex flex-col">
                <span className="text-2xl font-bold text-white leading-tight">
                  Tech5sages Global
                </span>
                <span className="text-xs text-[#0EA5E9] font-medium tracking-widest uppercase">
                  Pvt. Ltd.
                </span>
              </div>
            </Link>
            <p className="text-white/70 text-sm leading-relaxed mb-6 max-w-sm">
              Transforming careers and building futures. Tech5sages Global is your trusted partner in recruitment and talent acquisition, connecting exceptional talent with leading organizations worldwide.
            </p>
            
            {/* Contact Info */}
            <div className="space-y-3">
              <a href="tel:01353667547" className="flex items-center gap-3 text-white/70 hover:text-[#0EA5E9] transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#0EA5E9]/20 transition-colors">
                  <Phone className="h-4 w-4" />
                </div>
                <span className="text-sm">01353667547</span>
              </a>
              <a href="mailto:contact@tech5sages.com" className="flex items-center gap-3 text-white/70 hover:text-[#0EA5E9] transition-colors group">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center group-hover:bg-[#0EA5E9]/20 transition-colors">
                  <Mail className="h-4 w-4" />
                </div>
                <span className="text-sm">contact@tech5sages.com</span>
              </a>
              <div className="flex items-start gap-3 text-white/70">
                <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-4 w-4" />
                </div>
                <span className="text-sm">17 AWHO Jawahar Nagar, Indra Nagar, Dehradun, Uttarakhand – 248006, India</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-white/60 hover:text-[#0EA5E9] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Services</h4>
            <ul className="space-y-3">
              {services.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-white/60 hover:text-[#0EA5E9] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Resources</h4>
            <ul className="space-y-3">
              {resources.map((link) => (
                <li key={link.path}>
                  <Link 
                    to={link.path} 
                    className="text-white/60 hover:text-[#0EA5E9] text-sm transition-colors flex items-center gap-2 group"
                  >
                    <ArrowRight className="h-3 w-3 opacity-0 -ml-5 group-hover:opacity-100 group-hover:ml-0 transition-all" />
                    {link.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Follow Us - Social Links */}
          <div>
            <h4 className="text-white font-semibold mb-5 text-sm uppercase tracking-wider">Follow Us</h4>
            {activeSocialLinks.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {activeSocialLinks.map((social) => (
                  <a
                    key={social.key}
                    href={socialLinks[social.key]}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 rounded-lg bg-white/10 flex items-center justify-center text-white/60 hover:bg-[#0EA5E9] hover:text-white transition-all duration-300"
                    aria-label={social.label}
                    data-testid={`footer-social-${social.key}`}
                  >
                    <social.icon className="h-5 w-5" />
                  </a>
                ))}
              </div>
            ) : (
              <p className="text-white/40 text-sm">Social links coming soon</p>
            )}
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-white/50 text-sm">
            © {currentYear} Tech5sages Global Pvt. Ltd. All rights reserved.
          </p>
          
          {/* Bottom Social Links (for mobile visibility) */}
          {activeSocialLinks.length > 0 && (
            <div className="flex items-center gap-3 md:hidden">
              {activeSocialLinks.map((social) => (
                <a
                  key={social.key}
                  href={socialLinks[social.key]}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center text-white/60 hover:bg-[#0EA5E9] hover:text-white transition-all duration-300"
                  aria-label={social.label}
                >
                  <social.icon className="h-4 w-4" />
                </a>
              ))}
            </div>
          )}
        </div>
      </div>
    </footer>
  );
};

export default Footer;
